import {
  Box,
  Button,
  Circle,
  Flex,
  type FlexProps,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import { faCheck, faCloudArrowUp, faRotateRight, faStop } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import logger from '@/utils/logger'
import formatDuration from '@/utils/time/formatDuration'
import type { FileUploadTarget } from '@/server/services/userUpload'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import useCallbackRef from '@/client/hooks/useCallbackRef'
import { useStateRef } from '@/client/hooks/useStateRef'
import { MEDIA_RECORDER_CODECS } from '@/client/services/userUpload/constants'
import fixRecordedVideoDuration from '@/client/services/userUpload/fixRecordedVideoDuration'
import processUpload from '@/client/services/userUpload/processUpload'
import uploadFile from '@/client/services/userUpload/uploadFile'

import RichText from '@/components/UI/RichTextEditor/RichText'
import Card from '@/components/Worlds/UI/Card'

import { FormContext, type FormInputType } from '../types'

type RecordingState =
  | {
      type: 'idle'
    }
  | {
      type: 'awaiting-permission'
    }
  | {
      type: 'ready-to-record'
      mediaDeviceWatcher: () => void
    }
  | {
      type: 'recording'
      stream: MediaStream
      recorder: MediaRecorder
      buffer: Blob[]
      startedAt: Date
    }
  | {
      type: 'recorded'
      recordingBuffer: Blob
      recordingLocalUrl: string
    }
  | {
      type: 'uploaded'
      recordingUrl: string
    }
  | {
      type: 'error'
      message: string
    }

function getMediaDeviceKey(mediaDevice: MediaDeviceInfo) {
  return `${mediaDevice.deviceId}-${mediaDevice.kind}`
}

function MediaDevicesSelector({
  options,
  label,
  onChange,
}: {
  options: MediaDeviceInfo[]
  label: string
  onChange: (mediaDevice: MediaDeviceInfo) => void
}) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const mediaDevice = options.find(device => getMediaDeviceKey(device) === e.target.value)
      if (mediaDevice) {
        onChange(mediaDevice)
      }
    },
    [options, onChange]
  )

  return (
    <Box mb={4} pl={4}>
      <Text fontSize="sm" fontWeight="bold" color="fg" mb={2}>
        {label}
      </Text>
      <Box
        as="select"
        border="1px solid"
        borderColor="border"
        borderRadius="md"
        p={2}
        w="100%"
        bg="bg"
        color="fg"
        fontSize="sm"
        onChange={handleChange}
      >
        {options.map(device => (
          <option key={device.deviceId} value={getMediaDeviceKey(device)}>
            {device.label}
          </option>
        ))}
      </Box>
    </Box>
  )
}

function FormInputVideoOverlayContainer({
  children,
  ...props
}: React.PropsWithChildren<FlexProps>) {
  return (
    <Flex
      position="absolute"
      top={0}
      left={0}
      right={0}
      p={4}
      justify="space-between"
      align="center"
      bg="gray.emphasized/0.8"
      color="fg.inverted"
      {...props}
    >
      {children}
    </Flex>
  )
}

const FormInputVideoOverlayError: React.FC<{
  error: string
}> = ({ error }) => (
  <FormInputVideoOverlayContainer color="fg.error" fontSize="14px">
    Error: {error}
  </FormInputVideoOverlayContainer>
)

const FormInputVideoOverlayRecording: React.FC<{
  maxDurationSeconds?: number
  startedAt: Date
  onStop: () => void
}> = ({ maxDurationSeconds, startedAt, onStop }) => {
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      const durationInSeconds = Math.floor((now.getTime() - startedAt.getTime()) / 1000)
      setDuration(durationInSeconds)
    }, 1000)

    return () => clearInterval(interval)
  }, [startedAt])

  return (
    <FormInputVideoOverlayContainer>
      <Flex align="center" gap={2}>
        <Circle size="10px" bg="error.solid" />
        <Text fontWeight="bold">{formatDuration(duration)}</Text>
        {maxDurationSeconds == null ? null : (
          <Text fontSize="sm" color="fg">
            / {formatDuration(maxDurationSeconds)}
          </Text>
        )}
      </Flex>
      <Button
        aria-label="Stop recording"
        onClick={onStop}
        variant="danger"
        size="sm"
        rightIcon={<FontAwesomeIcon icon={faStop} />}
      >
        Stop Recording
      </Button>
    </FormInputVideoOverlayContainer>
  )
}

const FormInputVideoOverlayRecorded: React.FC<{
  target: FileUploadTarget
  recordingBuffer: Blob
  onReset: () => void
  onUploaded: (recordingUrl: string) => void
}> = ({ target, recordingBuffer, onReset, onUploaded }) => {
  const uploadCancelled = useRef(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [triedOnce, setTriedOnce] = useState(false)

  const [uploadVideo, isUploadingVideo] = useAPIWrapper(
    async () => {
      const fileName = recordingBuffer.type.startsWith('video/webm')
        ? 'recording.webm'
        : 'recording.mp4'
      const file = new File([recordingBuffer], fileName, { type: recordingBuffer.type })

      setUploadProgress(0)
      setTriedOnce(true)
      const { downloadUrl, userUploadId } = await uploadFile({
        file,
        fileType: recordingBuffer.type,
        target,
        onProgress: setUploadProgress,
      })
      if (uploadCancelled.current) {
        // Upload was cancelled, no need to transition state
        return
      }

      processUpload({
        userUploadId,
      }).catch(err => {
        logger.error('Error processing upload', { err, userUploadId })
      })

      onUploaded(downloadUrl)
    },
    [onUploaded, recordingBuffer, target],
    'Upload Video'
  )

  const handleReset = useCallback(() => {
    uploadCancelled.current = true
    onReset()
  }, [onReset])

  useEffect(() => {
    uploadVideo().catch(err => {
      logger.error('Error uploading video', { err })
    })
  }, [uploadVideo])

  // NOTE: We hide the upload video button, unless the automatic upload fails
  // This is to give the user a chance to retry the upload

  return (
    <FormInputVideoOverlayContainer>
      <Button
        aria-label="Stop recording"
        onClick={handleReset}
        variant="danger"
        size="sm"
        rightIcon={<FontAwesomeIcon icon={faRotateRight} />}
      >
        Start Over
      </Button>
      {isUploadingVideo ? (
        <HStack color="fg.muted">
          <Text>Uploading... ({uploadProgress}%)</Text>
          <FontAwesomeIcon icon={faCloudArrowUp} />
        </HStack>
      ) : triedOnce ? (
        <Button
          aria-label="Upload Video"
          onClick={uploadVideo}
          colorScheme="red"
          variant="solid"
          size="sm"
          rightIcon={<FontAwesomeIcon icon={faCloudArrowUp} />}
          isDisabled={isUploadingVideo}
        >
          Upload Video
        </Button>
      ) : null}
    </FormInputVideoOverlayContainer>
  )
}

const FormInputVideoOverlayUploaded: React.FC<{
  onReset: () => void
}> = ({ onReset }) => (
  <FormInputVideoOverlayContainer>
    <Button
      aria-label="Stop recording"
      onClick={onReset}
      colorScheme="red"
      variant="danger"
      size="sm"
      rightIcon={<FontAwesomeIcon icon={faRotateRight} />}
    >
      Start Over
    </Button>
    <HStack color="fg.success">
      <FontAwesomeIcon icon={faCheck} />
      <Text>Uploaded</Text>
    </HStack>
  </FormInputVideoOverlayContainer>
)

const FormInputVideo: React.FC<{
  input: FormInputType & { type: 'video' }
}> = ({ input }) => {
  const { onChange } = useContext(FormContext)
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([])
  const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<MediaDeviceInfo | null>(null)
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<MediaDeviceInfo | null>(null)

  const [recordingState, setRecordingState, recordingStateRef] = useStateRef<RecordingState>(
    input.initialValue == null
      ? { type: 'idle' }
      : { type: 'uploaded', recordingUrl: input.initialValue }
  )

  // TODO: Move away from accepting newState in this callback
  // Accept "action" instead, like StepRecord in CertificationCreator
  const transitionToState = useCallback(
    (newState: RecordingState) => {
      const currentState = recordingStateRef.current

      if (currentState.type === 'recording') {
        currentState.recorder.stop()
        currentState.stream.getTracks().forEach(track => {
          track.stop()
        })
      }

      if (currentState.type === 'recorded') {
        globalThis.URL.revokeObjectURL(currentState.recordingLocalUrl)
      }

      if (currentState.type === 'ready-to-record') {
        navigator.mediaDevices.removeEventListener('devicechange', currentState.mediaDeviceWatcher)
      }

      if (newState.type === 'recorded') {
        if (currentState.type !== 'recording') {
          logger.warning('Tried to transition to recorded state from unexpected state', {
            currentState: currentState.type,
          })
          // Error recovery
          transitionToState({ type: 'idle' })
          return
        }

        const recordingDurationSeconds = (Date.now() - currentState.startedAt.getTime()) / 1000
        if (
          input.minDurationSeconds != null &&
          recordingDurationSeconds < input.minDurationSeconds
        ) {
          transitionToState({
            type: 'error',
            message: `Recording was too short. It should be at least ${formatDuration(input.minDurationSeconds)} long`,
          })
          return
        }
      }

      setRecordingState(newState)
    },
    [input.minDurationSeconds, recordingStateRef, setRecordingState]
  )

  const handleUploaded = useCallback(
    (recordingUrl: string) => {
      onChange(input.name, recordingUrl)
      transitionToState({ type: 'uploaded', recordingUrl })
    },
    [input.name, onChange, transitionToState]
  )

  const stopRecordingRef = useCallbackRef(() => {
    if (recordingState.type === 'recording') {
      const combinedBuffer = new Blob(recordingState.buffer, {
        type: recordingState.recorder.mimeType,
      })

      fixRecordedVideoDuration(combinedBuffer).then(
        recordingBuffer => {
          transitionToState({
            type: 'recorded',
            recordingBuffer,
            recordingLocalUrl: URL.createObjectURL(recordingBuffer),
          })
        },
        err => {
          logger.error('Error fixing recorded video duration in FormInputVideo', { err })
          transitionToState({
            type: 'recorded',
            recordingBuffer: combinedBuffer,
            recordingLocalUrl: URL.createObjectURL(combinedBuffer),
          })
        }
      )
    } else if (recordingState.type === 'ready-to-record') {
      transitionToState({ type: 'idle' })
    }
  }, [recordingState, transitionToState])

  const requestPermission = useCallback(async () => {
    if (recordingState.type !== 'idle' && recordingState.type !== 'error') {
      // Only allow recording from these two states
      logger.warning('Tried to start recording from unexpected state', {
        state: recordingState.type,
      })
      return
    }
    transitionToState({ type: 'awaiting-permission' })

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true })

      let gotDevicesAtLeastOnce = false
      const mediaDeviceWatcher = async () => {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices()
          gotDevicesAtLeastOnce = true

          const videoDevices = devices.filter(device => device.kind === 'videoinput')
          const audioDevices = devices.filter(device => device.kind === 'audioinput')
          setVideoInputDevices(videoDevices)
          setAudioInputDevices(audioDevices)
          setSelectedAudioDevice(audioDevices[0])
          setSelectedVideoDevice(videoDevices[0])
        } catch (devicesErr) {
          if (gotDevicesAtLeastOnce) {
            logger.error('Error enumerating devices', { err: devicesErr })
          } else {
            throw devicesErr
          }
        }
      }
      navigator.mediaDevices.addEventListener('devicechange', mediaDeviceWatcher)
      await mediaDeviceWatcher()

      transitionToState({ type: 'ready-to-record', mediaDeviceWatcher })
    } catch (err) {
      transitionToState({
        type: 'error',
        message: `connecting to a camera & mic: ${err?.message ?? err}`,
      })
    }
  }, [recordingState.type, transitionToState])

  const startRecording = useCallback(async () => {
    const audioDeviceToUse = selectedAudioDevice
    const videoDeviceToUse = selectedVideoDevice

    if (audioDeviceToUse == null || videoDeviceToUse == null) {
      return
    }

    let stream: MediaStream | null = null
    let recorder: MediaRecorder | null = null

    try {
      if (typeof navigator.mediaDevices.getUserMedia === 'undefined') {
        throw new Error('Feature unsupported in this browser')
      }

      stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: audioDeviceToUse.deviceId },
        video: { deviceId: videoDeviceToUse.deviceId, width: 640, height: 480 },
      })

      const buffer: Blob[] = []
      const mimeType = MEDIA_RECORDER_CODECS.find(item => MediaRecorder.isTypeSupported(item))
      if (mimeType == null) {
        throw new Error('No supported recording codec found')
      }

      recorder = new MediaRecorder(stream, { mimeType })
      recorder.ondataavailable = event => {
        if (event.data != null && event.data.size > 0) {
          buffer.push(event.data)
        }

        if (input.maxDurationSeconds != null && buffer.length >= input.maxDurationSeconds) {
          // Because each chunk is a second, we can assume we've hit our limit
          stopRecordingRef.current()
        }
      }
      recorder.start(1000)
      // ^ 1 second per chunk

      transitionToState({
        type: 'recording',
        stream,
        recorder,
        buffer,
        startedAt: new Date(),
      })
    } catch (err) {
      recorder?.stop()
      stream?.getTracks().forEach(track => {
        track.stop()
      })
      // ^ In case we start recording but fail somewhere else, close the stream
      logger.error('Error starting recording in FormInputVideo', { err })

      transitionToState({
        type: 'error',
        message: `Something went wrong while recording: ${err?.message ?? err}`,
      })
    }
  }, [
    input.maxDurationSeconds,
    selectedAudioDevice,
    selectedVideoDevice,
    stopRecordingRef,
    transitionToState,
  ])

  const handleReset = useCallback(() => {
    transitionToState({ type: 'idle' })
    onChange(input.name, null)
  }, [onChange, input.name, transitionToState])

  const videoRecordingRef = useCallback(
    (element: HTMLVideoElement | null) => {
      if (element == null || recordingState.type !== 'recording') {
        return
      }
      element.srcObject = recordingState.stream
      element.muted = true
      element.play().catch(err => {
        logger.error('Error playing the currently recorded video in FormInputVideo', { err })
      })
    },
    [recordingState]
  )

  const videoRecordedRef = useCallback(
    (element: HTMLVideoElement | null) => {
      if (element == null || recordingState.type !== 'recorded') {
        return
      }
      element.src = recordingState.recordingLocalUrl
      element.muted = false
    },
    [recordingState]
  )

  // Running cleanup on unmount, in case we're recording and user navigates away
  useEffect(
    () => () => {
      stopRecordingRef.current()
    },
    [stopRecordingRef]
  )

  return (
    <Stack direction={['column', 'row']} spacing="16px">
      <Flex flex={2} direction="column">
        <Card>
          <Heading size="md" as="h2">
            What to record
          </Heading>
          <Box fontSize="14px" mt="4px">
            <RichText content={input.instructions} />
          </Box>
        </Card>
        {input.instructionsHint == null ? null : (
          <Box fontSize="14px" mt="8px" pl="24px" pr="24px">
            <RichText content={input.instructionsHint} />
          </Box>
        )}
      </Flex>
      <Flex
        flex={3}
        justify="center"
        align="center"
        border="3px dashed"
        borderColor="border"
        borderRadius="8px"
        minHeight="350px"
        backgroundColor="bg.subtle"
        position="relative"
      >
        {recordingState.type === 'idle' ||
        recordingState.type === 'error' ||
        recordingState.type === 'awaiting-permission' ? (
          <>
            {recordingState.type === 'awaiting-permission' ? (
              <Text>Waiting for permission...</Text>
            ) : null}
            {recordingState.type === 'idle' || recordingState.type === 'error' ? (
              <Button onClick={requestPermission}>Request Recording Permission</Button>
            ) : null}
            {recordingState.type === 'error' ? (
              <FormInputVideoOverlayError error={recordingState.message} />
            ) : null}
          </>
        ) : null}
        {recordingState.type === 'ready-to-record' ? (
          <Flex flexDirection="column">
            <MediaDevicesSelector
              options={videoInputDevices}
              label="Select Your Camera"
              onChange={setSelectedVideoDevice}
            />
            <MediaDevicesSelector
              options={audioInputDevices}
              label="Select Your Microphone"
              onChange={setSelectedAudioDevice}
            />
            <Flex justify="center" w="100%">
              <Button onClick={startRecording}>Record a Video</Button>
            </Flex>
          </Flex>
        ) : null}
        {recordingState.type === 'recording' ? (
          <>
            <video ref={videoRecordingRef} width="100%" playsInline />
            <FormInputVideoOverlayRecording
              startedAt={recordingState.startedAt}
              maxDurationSeconds={input.maxDurationSeconds}
              onStop={stopRecordingRef.current}
            />
          </>
        ) : null}
        {recordingState.type === 'recorded' ? (
          <>
            <video controls ref={videoRecordedRef} width="100%" playsInline />
            <FormInputVideoOverlayRecorded
              target={input.target}
              recordingBuffer={recordingState.recordingBuffer}
              onReset={handleReset}
              onUploaded={handleUploaded}
            />
          </>
        ) : null}
        {recordingState.type === 'uploaded' ? (
          <>
            <video controls src={recordingState.recordingUrl} width="100%" />
            <FormInputVideoOverlayUploaded onReset={handleReset} />
          </>
        ) : null}
      </Flex>
    </Stack>
  )
}

export default FormInputVideo
